import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { INDICATOR_GALLERIES } from '../data/indicatorImages';

const AUTOPLAY_MS = 2800;
const FADE_EASE = [0.22, 1, 0.36, 1];
const FADE_MS = 0.85;

const CATEGORIES = [
  {
    id: 'levels',
    labelFr: 'Levels Tools',
    labelEn: 'Levels Tools',
    images: [
      INDICATOR_GALLERIES.ORB[0],
      INDICATOR_GALLERIES.EnhancedSessionLevels[0],
      INDICATOR_GALLERIES.OpenLevels[0],
      INDICATOR_GALLERIES.AsianSession[0],
    ],
  },
  {
    id: 'volume',
    labelFr: 'Volume Profile',
    labelEn: 'Volume Profile',
    images: [
      INDICATOR_GALLERIES.PremiumVolumeProfile[0],
      INDICATOR_GALLERIES.PremiumVolumeProfile[1],
      INDICATOR_GALLERIES.LiteVolumeProfile[0],
    ],
  },
  {
    id: 'vwap',
    labelFr: 'VWAP',
    labelEn: 'VWAP',
    images: [
      INDICATOR_GALLERIES.PremiumVwap[0],
      INDICATOR_GALLERIES.PremiumVwap[1],
      INDICATOR_GALLERIES.LiteVwap[0],
    ],
  },
  {
    id: 'sessions',
    labelFr: 'Sessions',
    labelEn: 'Sessions',
    images: [...INDICATOR_GALLERIES.TradingSessions],
  },
];

const LINE_POINTS =
  'M24,210 L70,218 L110,188 L150,198 L190,168 L230,178 L270,148 L310,158 ' +
  'L350,128 L390,140 L430,112 L470,124 L510,96 L550,108 L590,78 L640,62';

const AREA_PATH = `${LINE_POINTS} L640,240 L24,240 Z`;

const FallbackChart = () => (
  <svg viewBox="0 0 664 260" className="h-full w-full" aria-hidden="true">
    <defs>
      <linearGradient id="heroAreaLinear" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#5B9FD4" stopOpacity="0.22" />
        <stop offset="100%" stopColor="#5B9FD4" stopOpacity="0" />
      </linearGradient>
    </defs>
    <rect width="664" height="260" fill="#0F1115" />
    {[60, 120, 180].map((y) => (
      <line
        key={y}
        x1="0"
        y1={y}
        x2="664"
        y2={y}
        stroke="rgba(255,255,255,0.04)"
        strokeWidth="1"
      />
    ))}
    <path d={AREA_PATH} fill="url(#heroAreaLinear)" />
    <path
      d={LINE_POINTS}
      fill="none"
      stroke="#5B9FD4"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Cadre produit Linear-like : catégories d'indicateurs + carrousel d'images.
 */
const HeroChart = ({ className = '' }) => {
  const { i18n } = useTranslation();
  const isFr = i18n.language === 'fr';

  const [categoryIndex, setCategoryIndex] = useState(0);
  const [imageIndex, setImageIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [failedImages, setFailedImages] = useState({});
  const [slideDir, setSlideDir] = useState(1);

  const categories = useMemo(
    () =>
      CATEGORIES.map((cat) => ({
        ...cat,
        label: isFr ? cat.labelFr : cat.labelEn,
      })),
    [isFr]
  );

  const activeCategory = categories[categoryIndex];
  const activeImage = activeCategory.images[imageIndex];
  const imageKey = `${activeCategory.id}-${imageIndex}`;
  const showFallback = Boolean(failedImages[imageKey]);

  const selectCategory = useCallback((index) => {
    const next = (index + CATEGORIES.length) % CATEGORIES.length;
    setSlideDir(next >= categoryIndex ? 1 : -1);
    setCategoryIndex(next);
    setImageIndex(0);
  }, [categoryIndex]);

  const goToImage = useCallback(
    (index) => {
      const len = activeCategory.images.length;
      const next = (index + len) % len;
      setSlideDir(next >= imageIndex ? 1 : -1);
      setImageIndex(next);
    },
    [activeCategory.images.length, imageIndex]
  );

  // Précharge les images pour éviter les coupures sèches
  useEffect(() => {
    CATEGORIES.forEach((cat) => {
      cat.images.forEach((src) => {
        const img = new Image();
        img.src = src;
      });
    });
  }, []);

  // Autoplay : enchaîne les images de la catégorie, puis passe à la suivante
  useEffect(() => {
    if (paused) return undefined;
    const id = window.setInterval(() => {
      const imageCount = CATEGORIES[categoryIndex].images.length;
      setSlideDir(1);
      if (imageIndex + 1 < imageCount) {
        setImageIndex(imageIndex + 1);
      } else {
        setCategoryIndex((prevCat) => (prevCat + 1) % CATEGORIES.length);
        setImageIndex(0);
      }
    }, AUTOPLAY_MS);
    return () => window.clearInterval(id);
  }, [paused, categoryIndex, imageIndex]);

  return (
    <motion.div
      className={`relative w-full overflow-hidden rounded-t-card border border-b-0 border-white/[.08] bg-surface shadow-[0_-24px_80px_rgba(0,0,0,0.45)] ${className}`}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      role="region"
      aria-roledescription="carousel"
      aria-label={isFr ? 'Aperçu des indicateurs Alturion' : 'Alturion indicators preview'}
    >
      <div className="flex items-center gap-3 border-b border-white/[.06] px-4 py-3">
        <div className="flex gap-1.5" aria-hidden="true">
          <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
        </div>
        <div className="flex-1 text-center">
          <span className="text-[11px] tracking-wide text-muted">Alturion · NinjaTrader 8</span>
        </div>
        <div className="w-10" />
      </div>

      <div className="flex min-h-[280px] md:min-h-[440px] lg:min-h-[500px] xl:min-h-[540px]">
        <aside className="hidden w-52 shrink-0 border-r border-white/[.06] bg-bg/60 p-5 sm:block">
          <p className="mb-3 text-[10px] font-medium uppercase tracking-[0.16em] text-muted">
            {isFr ? 'Indicateurs' : 'Indicators'}
          </p>
          <div className="space-y-1.5" role="tablist" aria-label={isFr ? 'Catégories' : 'Categories'}>
            {categories.map((category, index) => {
              const isActive = index === categoryIndex;
              return (
                <button
                  key={category.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => selectCategory(index)}
                  className={`w-full rounded-btn px-2.5 py-1.5 text-left text-[12px] transition-colors ${
                    isActive
                      ? 'bg-white/[.06] text-body'
                      : 'text-muted hover:bg-white/[.03] hover:text-body'
                  }`}
                >
                  {category.label}
                </button>
              );
            })}
          </div>
        </aside>

        <div className="relative min-w-0 flex-1 overflow-hidden bg-bg">
          <AnimatePresence initial={false} custom={slideDir}>
            <motion.div
              key={imageKey}
              custom={slideDir}
              className="absolute inset-0"
              initial={{ opacity: 0, x: slideDir * 28, scale: 1.02 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: slideDir * -20, scale: 1.01 }}
              transition={{
                opacity: { duration: FADE_MS, ease: FADE_EASE },
                x: { duration: FADE_MS, ease: FADE_EASE },
                scale: { duration: FADE_MS * 1.15, ease: FADE_EASE },
              }}
            >
              {!showFallback ? (
                <img
                  src={activeImage}
                  alt={activeCategory.label}
                  className="h-full w-full object-cover object-center"
                  draggable={false}
                  onError={() =>
                    setFailedImages((prev) => ({ ...prev, [imageKey]: true }))
                  }
                />
              ) : (
                <FallbackChart />
              )}
            </motion.div>
          </AnimatePresence>

          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-28 bg-gradient-to-t from-bg via-bg/40 to-transparent" />

          <div className="absolute inset-x-0 bottom-0 z-[2] flex items-end justify-between gap-3 p-4">
            <p className="text-[12px] font-medium text-body/90 sm:hidden">
              {activeCategory.label}
            </p>
            <div
              className="ml-auto flex items-center gap-1.5"
              role="group"
              aria-label={isFr ? 'Images de la catégorie' : 'Category images'}
            >
              {activeCategory.images.map((_, index) => (
                <button
                  key={`${activeCategory.id}-dot-${index}`}
                  type="button"
                  aria-label={`${activeCategory.label} ${index + 1}`}
                  aria-current={index === imageIndex ? 'true' : undefined}
                  onClick={() => goToImage(index)}
                  className={`h-1.5 rounded-full transition-all duration-500 ease-out ${
                    index === imageIndex
                      ? 'w-5 bg-body'
                      : 'w-1.5 bg-white/25 hover:bg-white/40'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default HeroChart;
